import React, { useEffect, useState } from 'react'
import { Card, Table, Button, Select, Form, Row, Col, Divider, DatePicker, Checkbox, message } from 'antd'
import usePsychologicalSupport from '../hooks/usePsychologicalSupport'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'

const { Option } = Select

const PsychologicalSupport = ({ value = [], onChange }) => {
  const [form] = Form.useForm()
  const { resources, loading } = usePsychologicalSupport()
  const [selectedResources, setSelectedResources] = useState(value)

  useEffect(() => {
    if (value && value.length > 0) {
      setSelectedResources(value)
    }
  }, [value])

  const handleAddResource = () => {
    form.validateFields().then((values) => {
      const selectedResource = resources.find((r) => r.id === values.resourceId)

      const newResource = {
        id: values.resourceId,
        name: selectedResource?.name || '',
        description: selectedResource?.description || '',
        type: selectedResource?.type || '',
        recommendedDate: values.recommendedDate,
        priority: values.priority,
        followUp: values.followUp,
        notes: values.notes,
        contactInfo: selectedResource?.contactInfo || '',
        completed: false
      }

      const updatedResources = [...selectedResources, newResource]
      setSelectedResources(updatedResources)
      onChange(updatedResources)

      form.resetFields()
      message.success('Resource added successfully')
    })
  }

  const handleRemoveResource = (resourceId) => {
    const updatedResources = selectedResources.filter((r) => r.id !== resourceId)
    setSelectedResources(updatedResources)
    onChange(updatedResources)
    message.success('Resource removed successfully')
  }

  const columns = [
    {
      title: 'Resource',
      dataIndex: 'name',
      key: 'name',
      width: '15%'
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: '10%'
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: '20%'
    },
    {
      title: 'Recommended Date',
      dataIndex: 'recommendedDate',
      key: 'recommendedDate',
      width: '12%',
      render: (date) => (date ? date.format('DD/MM/YYYY') : '')
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: '8%'
    },
    {
      title: 'Follow Up',
      dataIndex: 'followUp',
      key: 'followUp',
      width: '10%',
      render: (followUp) => (followUp ? 'Yes' : 'No')
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      width: '15%'
    },
    {
      title: 'Contact Info',
      dataIndex: 'contactInfo',
      key: 'contactInfo',
      width: '15%'
    },
    {
      title: 'Action',
      key: 'action',
      width: '6%',
      render: (_, record) => (
        <Button icon={<DeleteOutlined />} danger type='link' onClick={() => handleRemoveResource(record.id)} />
      )
    }
  ]

  return (
    <Card title='Psychological & Emotional Support' bordered={false}>
      <Form form={form} layout='vertical'>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name='resourceId'
              label='Resource'
              rules={[{ required: true, message: 'Please select a resource' }]}
            >
              <Select placeholder='Select resource' loading={loading} showSearch optionFilterProp='children'>
                {resources.map((resource) => (
                  <Option key={resource.id} value={resource.id}>
                    {resource.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              name='recommendedDate'
              label='Recommended Date'
              rules={[{ required: true, message: 'Please select a date' }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name='priority' label='Priority' rules={[{ required: true, message: 'Please select priority' }]}>
              <Select placeholder='Select priority'>
                <Option value='High'>High</Option>
                <Option value='Medium'>Medium</Option>
                <Option value='Low'>Low</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name='followUp' label='Requires Follow-up' valuePropName='checked'>
              <Checkbox />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name='notes' label='Notes'>
              <Select placeholder='Select notes'>
                <Option value='Initial consultation recommended'>Initial consultation recommended</Option>
                <Option value='Weekly sessions advised'>Weekly sessions advised</Option>
                <Option value='Use in conjunction with physical therapy'>
                  Use in conjunction with physical therapy
                </Option>
                <Option value='Recommended by specialist'>Recommended by specialist</Option>
                <Option value='Patient requested'>Patient requested</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24} style={{ textAlign: 'right' }}>
            <Button type='primary' onClick={handleAddResource} icon={<PlusOutlined />}>
              Add Resource
            </Button>
          </Col>
        </Row>
      </Form>

      <Divider />

      <Table columns={columns} dataSource={selectedResources} rowKey='id' pagination={false} scroll={{ y: 300 }} />

      {selectedResources.length === 0 && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          No psychological or emotional support resources added yet. Use the form above to add resources to the
          rehabilitation plan.
        </div>
      )}
    </Card>
  )
}

export default PsychologicalSupport
